import { CloseIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
import { Box, HStack, IconButton, Stack, Text, VStack } from "@chakra-ui/react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { BaseAudio, sumDurationToTime } from "../../utils/audio";
import { Category } from "../../utils/vtt";
import CategoryItem from "./CategoryItem";

type Props = {
  audio: BaseAudio;
  category?: Category;
  index: number;
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
  onLeaveEdit: (_: null) => void;
  onClick: (audio: BaseAudio) => void;
  isSelected?: boolean;
  isDisabled?: boolean;
};

export default function AudioItem({
  audio,
  category,
  index,
  onEdit,
  onDelete,
  onLeaveEdit,
  onClick,
  isSelected,
  isDisabled = false,
}: Props) {
  const { t } = useTranslation();
  const _onClick = useCallback(() => onClick(audio), [onClick, audio]);
  const _onEdit = useCallback(() => onEdit(index), [onEdit, index]);
  const _onDelete = useCallback(() => onDelete(index), [onDelete, index]);
  const _onLeaveEdit = useCallback(() => onLeaveEdit(null), [onLeaveEdit]);

  return (
    <Stack
      flexDirection="row"
      padding={4}
      bg={isSelected ? "orange.100" : "transparent"}
    >
      <HStack flexGrow={1}>
        <Box paddingX={4}>
          <Text fontSize="lg">#{index}</Text>
        </Box>
        <HStack flexGrow={1} gap={4} justifyContent="space-between">
          <Stack cursor="pointer" onClick={_onClick}>
            <VStack alignItems="start">
              <HStack>
                <Text fontSize="md" style={{ textDecoration: "underline" }}>
                  {t("common.labels.start")}: {audio.startTime} -{" "}
                  {t("common.labels.start")}:{" "}
                  {sumDurationToTime(audio.startTime, audio.duration)}
                </Text>
                {category && <CategoryItem category={category} />}
              </HStack>
              <audio preload="metadata" controls>
                <source src={audio.base64} type="audio/ogg" />
              </audio>
            </VStack>
            {audio.description && (
              <Text fontStyle="italic" fontSize={13}>
                {audio.description}
              </Text>
            )}
          </Stack>
          {!isDisabled && (
            <HStack gap={2}>
              <IconButton
                onClick={isSelected ? _onLeaveEdit : _onEdit}
                aria-label={
                  isSelected ? "Leave edit subtitle" : "Edit subtitle"
                }
                icon={isSelected ? <CloseIcon /> : <EditIcon />}
              />
              <IconButton
                bg="red.100"
                color="primary.DEFAULT"
                onClick={_onDelete}
                aria-label="Delete subtitle"
                icon={<DeleteIcon />}
              />
            </HStack>
          )}
        </HStack>
      </HStack>
    </Stack>
  );
}
